import { TableProductType } from 'components/template/PromoRecommendation/RecommendationMainSection';
import { renderTitle } from './getSalesAttributionColumns';
import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/BrandValueChartContainer/BrandValueChartContainer';
import EditPencilOutlined from 'assets/svg/EditPencilOutlined';
import DeleteOutlined from 'assets/svg/DeleteOutlined';
import { colorConstants } from 'config/colorConstants';
import HideEye from 'assets/svg/HideEyeIcon';
import UnHideEye from 'assets/svg/UnHideEyeIcon';
import CalenderEventItem from 'components/molecules/CalenderEventItem';
import MinusSvgComponent from 'assets/svg/Minus';
import { CSSProperties, StyleHTMLAttributes, useState } from 'react';
import _ from 'lodash';
import { calculateFontSize, formatNumWithKMBAlt, getContainerWidth, useMainContainerWidth } from './helperMethods';
import PositiveUpArrow from 'assets/svg/PositiveUpArrow';
import NegativeDownArrow from 'assets/svg/NegativeDownArrow';
import { Popover, Tooltip } from 'antd';
import UpAltSvg from 'assets/svg/UpAlt';
import DownAltSvg from 'assets/svg/DownAlt';
import styled from '@emotion/styled';
import { trackEventMixpanel } from './mixpanel';
import {
    PROMO_RECOMMENDATION_DELETE_EVENT_CLICKED,
    PROMO_RECOMMENDATION_EDIT_EVENT_CLICKED,
} from './mixpanelEventNames';

const disabledColor = colorConstants.greyAlt4;

export const getRecommendationEventTableColumns = (
    data: any[],
    expandedProducts: TableProductType,
    setExpandedProducts: React.Dispatch<React.SetStateAction<TableProductType>>,
    selectedIndex: number | null,
    deleteEvent: Function,
    editPlan: Function,
    setShowInputNameModal: Function,
    setEditNameInfo: Function,
    enableEventNameEdit: boolean,
) => {
    let result: any[] = [];

    const columns = data && Array.isArray(data) && data.length > 0 ? Object.keys(data[0]).slice(0, -2) : [];

    result = columns.map((key, idx) => {
        const isLastItem = key === 'edit';
        const isProduct = key === 'Products';
        const isAccount = key === 'Account';
        const isAllEvent = key === 'All Event';
        return {
            title: isProduct ? (
                <div className="flex gap-1 items-center justify-center">
                    <span className="text-purpleFont font-bold font-xs">Products</span>{' '}
                    <span className="cursor-pointer">
                        {expandedProducts?.[key] === 'all' ? (
                            <span
                                onClick={() => {
                                    setExpandedProducts({ ...expandedProducts, [key]: null });
                                }}
                            >
                                <UnHideEye />
                            </span>
                        ) : (
                            <span
                                onClick={() => {
                                    setExpandedProducts({ ...expandedProducts, [key]: 'all' });
                                }}
                            >
                                <HideEye />
                            </span>
                        )}
                    </span>
                </div>
            ) : (
                renderTitle(
                    isLastItem ? '' : key,
                    null,
                    undefined,
                    'text-purpleFont !font-bold',
                    graphXAxisLabelFontSize,
                )
            ),
            dataIndex: key,
            width: isLastItem ? 30 : 110,
            key: key,
            onCell: (record: any, rowIndex: any) => {
                let style: any = {};
                if (rowIndex === expandedProducts || expandedProducts?.['Products'] === 'all') {
                    style = { ...style, background: colorConstants.lightPurple };
                }
                //highlight the new added/edited event
                if (rowIndex === selectedIndex) {
                    style = { ...style, background: colorConstants.grey };
                }
                if (rowIndex === selectedIndex && idx === 0) {
                    style = { ...style, borderLeft: `4px solid ${colorConstants.violet}` };
                }
                if (isProduct) {
                    style = { ...style, textAlign: 'center' };
                }
                return { style };
            },

            render: (columnData: any, rowData: any, rowIndex: number) => {
                //for delete and edit icon
                const isActive = rowData?.isActive === 'active';
                const commonClassName = isActive ? 'cursor-pointer' : 'pointer-events-none';
                if (isLastItem && columnData) {
                    return (
                        <div className="flex justify-center gap-2 items-center">
                            <span
                                className={commonClassName}
                                onClick={() => {
                                    if (!isActive) return;
                                    editPlan(rowData.edit);
                                    trackEventMixpanel(PROMO_RECOMMENDATION_EDIT_EVENT_CLICKED, {
                                        eventName: rowData?.['All Event'],
                                    });
                                }}
                            >
                                <EditPencilOutlined stroke={isActive ? colorConstants.violet : disabledColor} />
                            </span>
                            <span
                                onClick={() => {
                                    if (!isActive) return;
                                    deleteEvent(rowIndex, rowData?.key, rowData?.edit?.parentId);
                                    trackEventMixpanel(PROMO_RECOMMENDATION_DELETE_EVENT_CLICKED, {
                                        eventName: rowData?.['All Event'],
                                    });
                                }}
                                className={commonClassName}
                            >
                                <DeleteOutlined stroke={isActive ? colorConstants.red : disabledColor} />
                            </span>
                        </div>
                    );
                }

                //for products column
                if (isProduct && columnData) {
                    const isExpanded = expandedProducts?.[key] === 'all' || rowIndex === expandedProducts?.[key];
                    if (!isExpanded)
                        return (
                            <div className="flex justify-center">
                                <div
                                    style={{ backgroundColor: isActive ? colorConstants.violetAlt : disabledColor }}
                                    onClick={() => setExpandedProducts({ ...expandedProducts, [key]: rowIndex })}
                                    className={`flex leading-3 font-medium cursor-pointer justify-center items-center font-xxs circle-size rounded-[50%] text-white ${commonClassName}`}
                                >
                                    {columnData?.length}
                                </div>
                            </div>
                        );
                    return (
                        <div className={`font-xs  ${isActive ? 'text-greyFont' : ' text-fontGreyAlt1'} `}>
                            {columnData?.map((item: any) => (
                                <div key={item}>{item}</div>
                            ))}
                        </div>
                    );
                }

                if (isAllEvent) {
                    return (
                        <div
                            className={`font-xs cursor-pointer underline ${
                                isActive ? 'text-greyFont' : 'text-fontGreyAlt1'
                            }`}
                            onClick={
                                !isActive || !enableEventNameEdit
                                    ? () => {}
                                    : () => {
                                          setShowInputNameModal(true);
                                          setEditNameInfo({
                                              index: rowIndex,
                                              eventId: rowData.edit.eventId,
                                              eventName: rowData.edit.eventName,
                                          });
                                      }
                            }
                            role="button"
                            tabIndex={0}
                        >
                            {columnData}
                        </div>
                    );
                }

                //for account column
                if (isAccount && columnData) {
                    const isExpanded = rowIndex === expandedProducts?.[key];
                    if (!isExpanded)
                        return (
                            <div
                                className={`font-medium font-xs cursor-pointer ${commonClassName} ${
                                    isActive ? 'text-greyFont' : ' text-fontGreyAlt1'
                                }`}
                            >
                                <div onClick={() => setExpandedProducts({ ...expandedProducts, [key]: rowIndex })}>
                                    {columnData?.[0]}
                                    {columnData?.length > 1 ? '...' : ''}
                                </div>
                            </div>
                        );
                    return (
                        <div
                            onClick={() => setExpandedProducts({ ...expandedProducts, [key]: null })}
                            className={`font-medium font-xs ${commonClassName}`}
                        >
                            {columnData?.map((item: any) => (
                                <div key={item}>{item}</div>
                            ))}
                        </div>
                    );
                }

                return (
                    <div className={`font-xs ${isActive ? 'text-greyFont' : ' text-fontGreyAlt1'}`}>{columnData}</div>
                );
            },
        };
    });
    return result;
};

//for recommendation calender table
export const getRecommendationCalenderColumns = (
    data: any[],
    colWidth: number,
    eventsTableRowHeight: any[],
    leftOutHeight: number,
    selectedIndex: number | null,
    tooltipMapping: any[],
) => {
    const item = data ? data[0] : null;
    const result: any[] = [];
    const keyItems = data?.length > 0 ? Object.keys(item).slice(3) : []; //removing first item promo calendar
    const weekBorderStyle: any = {
        position: 'absolute',
        top: 0,
        bottom: 0,
        borderRight: `1px dashed ${colorConstants.borderGreyAlt}`,
    };
    if (keyItems && keyItems.length > 0) {
        const dateColumns =
            (keyItems &&
                keyItems.length > 0 &&
                keyItems.map((item, index) => {
                    return {
                        title: renderTitle(
                            item,
                            undefined,
                            undefined,
                            'text-purpleFont !font-bold',
                            graphXAxisLabelFontSize,
                        ),
                        dataIndex: item,
                        key: item,
                        align: 'left',
                        width: colWidth,
                        // colSpan: rowIndex === 0 ? (index === 0 ? keyItems.length + 1 : 0) : 1,
                        onCell: (record: any, rowIndex: any) => {
                            const isLastItem = record?.event === 'tooltip-calender';
                            return {
                                style: {
                                    background: rowIndex === selectedIndex ? colorConstants.grey : '', //highlight the new added/edited event
                                    height: isLastItem ? leftOutHeight : eventsTableRowHeight?.[rowIndex] || 0,
                                    paddingInline: 0,
                                    paddingBlock: rowIndex === 0 ? 0 : isLastItem ? 0 : 2,
                                    verticalAlign: 'top',
                                    borderRight: `1px solid ${colorConstants.borderGrey}`,
                                },
                            };
                        },
                        render: (colVal: any, rowVal: any, rowIndex: number) => {
                            const isActive = rowVal?.isActive === 'active';
                            return (
                                <>
                                    <div className="relative">
                                        {colVal?.length > 0 &&
                                            colVal.map((val: any, index: number) => {
                                                const style = val.style || {};
                                                return (
                                                    <CalenderEventItem
                                                        key={index}
                                                        item={val}
                                                        style={{
                                                            ...style,
                                                            pointerEvents: isActive ? 'auto' : 'none',
                                                            backgroundColor: isActive
                                                                ? style?.background
                                                                : disabledColor,
                                                        }}
                                                        overlayKeyMapping={tooltipMapping}
                                                    />
                                                );
                                            })}
                                    </div>

                                    <div>
                                        <div style={{ ...weekBorderStyle, width: (colWidth / 4) * 1 }}> </div>
                                        <div style={{ ...weekBorderStyle, width: (colWidth / 4) * 2 }}> </div>
                                        <div style={{ ...weekBorderStyle, width: (colWidth / 4) * 3 }}> </div>
                                    </div>
                                </>
                            );
                        },
                    };
                })) ||
            [];
        result.push(...dateColumns);
    }

    return result;
};
const lastColumnWidth = calculateFontSize(window.innerWidth, 185);
const restOfColumnWidth = calculateFontSize(window.innerWidth, 148);

const arrowKeyMapping: any = {
    positiveUp: {
        greater: <PositiveUpArrow />,
        lesser: <NegativeDownArrow />,
    },
    negativeUp: {
        greater: <PositiveUpArrow stroke={colorConstants.red} />,
        lesser: <NegativeDownArrow stroke={colorConstants.limeGreen} />,
    },
};

export const getKpiTableColumns = (
    data: any[],
    eventsTableRowHeight: any[],
    selectedIndex: number | null,
    selectedKpiList: any[],
    metricMeta: any = {},
) => {
    let result: any[] = [];
    let columns = data && Array.isArray(data) && data.length > 0 ? Object.keys(data[0]).slice(0, -3) : [];
    columns = columns.filter((column) => selectedKpiList.includes(column));
    const arrowArray: any = {};
    columns.forEach((column) => {
        const meta = metricMeta?.[column];
        if (meta) {
            arrowArray[column] = [meta.arrowConfig, meta.pre, meta.post];
        }
    });
    result = columns.map((key, idx) => {
        return {
            title: renderTitle(
                key,
                'center',
                undefined,
                'text-purpleFont !font-bold text-center',
                graphXAxisLabelFontSize,
            ),
            dataIndex: key,
            width: idx === columns.length - 1 ? lastColumnWidth : restOfColumnWidth,
            key: key,
            onCell: (record: any, rowIndex: any) => {
                let style: CSSProperties = { textAlign: 'center', height: eventsTableRowHeight?.[rowIndex] };
                // if (isProduct) {
                //     style = { ...style, textAlign: 'center' };
                // }
                //highlight the new added/edited event
                if (rowIndex === selectedIndex) {
                    style = { ...style, background: colorConstants.grey };
                }
                return { style };
            },

            render: (columnData: any, rowData: any, rowIndex: number) => {
                const isActive = rowData?.isActive === 'active';
                const arrowDetails = Object.keys(rowData?.arrowDetails)?.length > 0 ? rowData?.arrowDetails : null;
                // const symbol = String(key)?.toLowerCase().includes('volume') ? '' : commonConfig.units.currency;
                return (
                    <div className="flex gap-1 justify-center items-center">
                        {arrowDetails && !_.isNull(columnData) ? (
                            arrowKeyMapping?.[arrowArray[key]?.[0]]?.[arrowDetails[key]]
                        ) : (
                            <div style={{ width: 10 }}></div>
                        )}
                        <div className={`font-xs ${isActive ? 'text-greyFont' : ' text-fontGreyAlt1'}`}>{`${
                            _.isNull(columnData)
                                ? ''
                                : arrowArray[key]?.[1] + formatNumWithKMBAlt(columnData, 2, 2) + arrowArray[key]?.[2]
                        }`}</div>
                    </div>
                );
            },
        };
    });
    return result;
};

//summary table columns
export const getSummaryColumns = (data: any[] | null, setSelectedPlan: Function, selectedPlan: any) => {
    const containerWidth = getContainerWidth('null-id'); //giving null id so that it takes default container width
    let result: any[] = [];
    let columns = data && Array.isArray(data) && data.length > 0 ? Object.keys(data?.[0]) : [];
    columns = columns?.slice(0, columns.length - 2);

    result = columns.map((key, idx) => {
        return {
            title: renderTitle(
                key,
                idx === 0 ? null : 'center',
                undefined,
                'text-purpleFont !font-bold',
                graphXAxisLabelFontSize,
            ),
            dataIndex: key,
            width: containerWidth / 4 - 10 || 250,
            key: key,
            fixed: idx === 0 ? 'left' : false,
            align: idx === 0 ? 'left' : 'center',
            onCell: (record: any, index: number) => {
                const isRowEqual = selectedPlan?.KPI === record?.KPI;
                return {
                    onClick: () => {
                        if (isRowEqual) setSelectedPlan(undefined);
                        else setSelectedPlan(record);
                    },
                    style: {
                        cursor: 'pointer',
                        backgroundColor: isRowEqual ? colorConstants.greyAlt1 : 'initial',
                    },
                };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                if (typeof columnData === 'string') {
                    return <div className="font-xs text-greyFont font-medium">{columnData}</div>;
                }
                return (
                    <>
                        {!_.isNull(columnData?.val) && (
                            <div className="text-greyFont font-xs">
                                {columnData?.pre}
                                {formatNumWithKMBAlt(columnData?.val, 2, 2)}
                                {columnData?.post}
                            </div>
                        )}
                    </>
                );
            },
        };
    });
    return result;
};

//playbook section table columns
export const getPlaybookColumns = (data: any[]) => {
    let result: any[] = [];
    let columns = data && Array.isArray(data) && data.length > 0 ? Object.keys(data?.[0]) : [];
    result = columns.map((key, idx) => {
        return {
            title: (
                <div className="text-purpleFont font-xs font-bold">
                    <div style={{ paddingLeft: idx === 0 ? 12 : '' }}>{key}</div>
                    <div>
                        {idx === 0 ? (
                            '    '
                        ) : (
                            <p className="font-normal">
                                Guidelines<span style={{ color: colorConstants.lightGrey }}> | </span>Projected{' '}
                            </p>
                        )}
                    </div>
                </div>
            ),
            dataIndex: key,
            width: 250,
            key: key,
            fixed: idx === 0 ? 'left' : false,
            align: idx === 0 ? 'left' : 'center',

            render: (columnData: any, rowData: any, rowIndex: number) => {
                const isTotal = rowData?.Product === 'Total';
                if (typeof columnData === 'string') {
                    return <div className="font-xs text-greyFont font-medium">{columnData}</div>;
                }
                const { firstValue, secondValue, operator, pre, post } = columnData;
                const formattedFirstValue =
                    firstValue && Array.isArray(firstValue) ? firstValue : formatNumWithKMBAlt(firstValue, 2, 2);
                const formattedSecondValue = formatNumWithKMBAlt(secondValue, 2, 2);
                let value: 'negative' | 'positive' = 'positive';

                if (operator && operator === 'lesser_than') {
                    if (Number(firstValue) < Number(secondValue)) value = 'negative';
                } else if (operator === 'range' && Array.isArray(firstValue)) {
                    if (Number(secondValue) > Number(firstValue[1]) || Number(secondValue) < Number(firstValue[0]))
                        value = 'negative';
                } else if (operator && operator === 'greater_than') {
                    if (Number(firstValue) > Number(secondValue)) value = 'negative';
                }

                if (firstValue && Array.isArray(firstValue)) {
                    const updatedSecondValue = formatNumWithKMBAlt(secondValue, 0);
                    return (
                        <div className="flex items-center justify-center">
                            <div style={{ fontWeight: isTotal ? 500 : 400 }} className="text-greyFont font-xs">
                                {pre}
                                {`${formatNumWithKMBAlt(formattedFirstValue[0], 0)} - ${formatNumWithKMBAlt(
                                    formattedFirstValue[1],
                                    0,
                                )}`}
                                {post}
                                <span style={{ color: colorConstants.lightGrey }}> | </span>
                                {pre}
                                {updatedSecondValue}
                                {post}
                            </div>

                            <p
                                className="legend-size-square"
                                style={{
                                    background: isTotal
                                        ? 'transparent'
                                        : value === 'positive'
                                        ? colorConstants.limeGreen
                                        : colorConstants.red,
                                    borderRadius: 2,
                                    marginLeft: 8,
                                }}
                            ></p>
                        </div>
                    );
                }
                // if(firstValue)
                return (
                    <>
                        {!_.isNull(columnData?.val) && (
                            <div className="flex items-center justify-center">
                                <div style={{ fontWeight: isTotal ? 500 : 400 }} className="text-greyFont font-xs">
                                    {pre}
                                    {formattedFirstValue}
                                    {post}
                                    <span style={{ color: colorConstants.lightGrey }}> | </span>
                                    {pre}
                                    {formattedSecondValue}
                                    {post}
                                </div>
                                <p
                                    className="legend-size-square"
                                    style={{
                                        background: isTotal
                                            ? 'transparent'
                                            : value === 'positive'
                                            ? colorConstants.limeGreen
                                            : colorConstants.red,
                                        borderRadius: 2,
                                        marginLeft: 8,
                                    }}
                                ></p>
                            </div>
                        )}
                    </>
                );
            },
        };
    });
    return result;
};

export const getBestWeeksColumns = (
    headersData: any,
    selectedFilterItem: any,
    setSelectedFilterItem: Function,
    bestWeeksFilterItems: Array<any>,
    openFilterDropdown: boolean,
    setOpenFilterDropdown: Function,
) => {
    let result: any[] = [];
    const colWidth = 40;

    result = headersData?.map((header: any, idx: number) => {
        const monthLabel = header?.month;
        const periods: any[] = header?.periods;
        const events: any = header?.events;
        const weekBorderStyle: any = {
            position: 'absolute',
            top: 0,
            bottom: 0,
            borderRight: `1px dashed ${colorConstants.borderGreyAlt}`,
        };
        let tableHeaderHeight = 60;

        return {
            title:
                idx === 0 ? (
                    <div className="flex  flex-col relative" style={{ height: '120px' }}>
                        <span className="flex-grow"> </span>
                        <span style={{ borderTop: `1px solid ${colorConstants.borderGreyAlt}` }} className="h-6">
                            <Popover
                                autoAdjustOverflow
                                content={() => {
                                    return (
                                        <div style={{ minWidth: '100px' }}>
                                            {bestWeeksFilterItems.map((i) => (
                                                <div
                                                    key={i.key}
                                                    onClick={(e) => {
                                                        setSelectedFilterItem(i);
                                                        setOpenFilterDropdown(false);
                                                    }}
                                                    className={`cursor-pointer py-1 px-4 pr-8  ${
                                                        i.label === selectedFilterItem.label
                                                            ? 'bg-bgBackground font-medium'
                                                            : ''
                                                    }`}
                                                >
                                                    {i.label}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                }}
                                destroyTooltipOnHide
                                trigger="click"
                                onOpenChange={(e) => setOpenFilterDropdown(e)}
                                open={openFilterDropdown}
                                showArrow={false}
                                placement="bottomLeft"
                                overlayClassName="popover-padding-remove"
                            >
                                <span
                                    className={`flex gap-2 rounded justify-between items-center cursor-pointer px-1 mr-4 ${
                                        openFilterDropdown ? 'bg-darkViolet' : ''
                                    } `}
                                >
                                    {selectedFilterItem?.label} {openFilterDropdown ? <UpAltSvg /> : <DownAltSvg />}
                                </span>
                            </Popover>
                        </span>
                    </div>
                ) : (
                    <div className="mt-2">
                        <p className="text-left mb-3 text-purpleFont font-xs font-bold">{monthLabel}</p>
                        <div
                            style={{ height: tableHeaderHeight }}
                            className={`relative  ${idx === headersData.length - 1 ? 'overflow-hidden' : ''}`}
                        >
                            {periods?.map((period: number, periodIdx: number) => {
                                if (period in events) {
                                    return (
                                        <div
                                            style={{
                                                position: periodIdx === 0 ? 'relative' : 'absolute',
                                                top: 0,
                                                left: `${(periodIdx / periods?.length) * 100}%`,
                                                overflowY: 'scroll',
                                                height: '60px',
                                            }}
                                        >
                                            {events?.[period]?.map((event: any) => {
                                                return (
                                                    <div
                                                        title={event?.eventName}
                                                        className="mb-1 font-xxs font-medium"
                                                        style={{
                                                            width: 'fit-content',
                                                            position: 'relative',
                                                            whiteSpace: 'nowrap',
                                                            zIndex: 1,
                                                            color: 'white',
                                                            background: colorConstants.violetAlt1,
                                                            borderLeft: `2px solid ${colorConstants.violet}`,
                                                            textAlign: 'left',
                                                            padding: '4px 8px',
                                                            borderTopRightRadius: 4,
                                                            borderBottomRightRadius: 4,
                                                        }}
                                                    >
                                                        {event?.eventName}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                }
                            })}
                        </div>
                        <div className="flex ">
                            {periods.map((period: number) => {
                                return (
                                    <div
                                        className="font-xs p-1"
                                        style={{
                                            color: colorConstants.violet,
                                            flex: 1,
                                            borderRight: `1px solid ${colorConstants.borderGreyAlt}`,
                                            borderTop: `1px solid ${colorConstants.borderGreyAlt}`,
                                        }}
                                    >
                                        {period}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ),
            dataIndex: monthLabel,
            width: idx === 0 ? 225 : colWidth * periods?.length,
            key: monthLabel,
            fixed: idx === 0 ? 'left' : false,
            align: idx === 0 ? 'left' : 'center',
            onCell: (record: any, rowIndex: any) => {
                let style: React.CSSProperties = { padding: 0, border: '' };
                if (idx === 0) {
                    style = { ...style, paddingLeft: 8 };
                }
                return { style };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                //first column
                if (idx === 0) {
                    return (
                        <Tooltip
                            placement="bottomLeft"
                            color="white"
                            destroyTooltipOnHide
                            overlayStyle={{ borderRadius: 4 }}
                            overlayInnerStyle={{
                                fontSize: graphXAxisLabelFontSize,
                                color: colorConstants.darkGrey,
                            }}
                            title={rowData?.attributeName}
                        >
                            <div className="font-xs font-medium text-fontGray">
                                {rowData?.attributeName?.substring(0, 25)}
                                {rowData?.attributeName?.length > 25 ? '...' : ''}
                            </div>
                        </Tooltip>
                    );
                }

                return (
                    <div className="flex">
                        {periods?.map((period: number, periodIdx: number) => {
                            const valueObj = rowData?.values?.[period];
                            //rest of the rows
                            return (
                                <div
                                    className="font-xs p-1 h-8"
                                    style={{
                                        background: valueObj?.color,
                                        fontWeight: valueObj?.isBordered ? 500 : 400,
                                        color: valueObj?.isBordered ? colorConstants.fontGrey : colorConstants.darkGrey,
                                        flex: 1,
                                        border: _.isNil(valueObj?.val)
                                            ? ' '
                                            : valueObj?.isBordered
                                            ? `1px solid ${colorConstants.fontGrey}`
                                            : `1px solid ${colorConstants.borderGreyAlt}`,
                                    }}
                                >
                                    {_.isNil(valueObj?.val) ? '' : valueObj?.val}
                                </div>
                            );
                        })}
                    </div>
                );
            },
        };
    });
    return result;
};

export const getBestWeeksDrillDownColumns = (columns: any[]) => {
    let result: any[] = [];

    const tempColumns = columns?.map((header: any) => {
        return {
            title: <p className={`text-[14px] flex justify-center text-purpleFont`}>{header?.label}</p>,
            dataIndex: header?.key,
            key: header?.key,
            onCell: (record: any, rowIndex: number) => {},
            // sortKey: header?.sortKey, //adding a sort key so that on sort click we can read value from this key
            // sortOrder: header?.sortKey === sortObj?.sortBy ? sortObj?.sortOrder : null, //condition to highlight sort arrow according to selection
            // sorter: header?.sortKey ? true : false,
            // sortDirections: header?.key === 'updated_at' ? ['ascend', 'descend', 'ascend'] : ['ascend', 'descend'], //always have some sorting in last updated at field
            // width: 150,
            render: (columnData: any, rowData: any) => {
                return (
                    <div className="flex justify-center gap-2 items-center">
                        <div className="text-blackAlt">{columnData}</div>
                    </div>
                );
            },
        };
    });
    result.push(...tempColumns);
    return result;
};
